/**
 * Модальное окно авторизации
 * 
 * Отображает модальное окно с формой авторизации.
 * Содержит заголовок "Вход в личный кабинет", форму LoginForm и кнопку закрытия.
 * Адаптивно: на мобильных устройствах занимает 90% ширины экрана.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { LoginForm } from './LoginForm';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    maxWidth: '480px',
    width: '100%',
    margin: theme.spacing(2),
    // Адаптивность для мобильных устройств
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
      width: '90%',
      margin: theme.spacing(1),
      borderRadius: '8px',
    },
    // Адаптивность для очень маленьких экранов
    [theme.breakpoints.down(400)]: {
      maxWidth: '95%',
      width: '95%',
      margin: theme.spacing(0.5),
    },
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  position: 'relative',
  overflow: 'hidden',
  // Адаптивность для мобильных устройств
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  top: theme.spacing(2),
  color: theme.palette.grey[500],
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  [theme.breakpoints.down('sm')]: {
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

export interface LoginModalProps {
  /** Флаг открытия модального окна */
  open: boolean;
  /** Callback при закрытии модального окна */
  onClose: () => void;
  /** Callback при успешной авторизации */
  onSuccess: () => void;
}

/**
 * Модальное окно авторизации
 * 
 * Отображает модальное окно с формой авторизации LoginForm.
 * Содержит кнопку закрытия (крестик) в правом верхнем углу.
 * При успешной авторизации вызывает onSuccess и закрывает модальное окно.
 * 
 * Адаптивность:
 * - Desktop: ширина 480px
 * - Mobile: ширина 90%
 * 
 * @param open - Флаг открытия модального окна
 * @param onClose - Callback для закрытия модального окна
 * @param onSuccess - Callback при успешной авторизации (закрывает модальное окно)
 * 
 * @example
 * ```tsx
 * const [loginOpen, setLoginOpen] = useState(false);
 * 
 * <LoginModal
 *   open={loginOpen}
 *   onClose={() => setLoginOpen(false)}
 *   onSuccess={() => {
 *     setLoginOpen(false);
 *     // Дополнительная логика после успешной авторизации
 *   }}
 * />
 * ```
 */
export const LoginModal: React.FC<LoginModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const handleSuccess = () => {
    // Вызываем callback успешной авторизации
    onSuccess();
    // Закрываем модальное окно
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="login-modal-title"
      maxWidth={false}
    >
      <StyledDialogContent>
        {/* Кнопка закрытия */}
        <CloseButton
          aria-label="закрыть"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </CloseButton>

        {/* Форма авторизации */}
        <LoginForm onSuccess={handleSuccess} />
      </StyledDialogContent>
    </StyledDialog>
  );
};
